// The frontend only controls navigation. The server independently authorizes every request.
export const USER_TYPES = {
  ADMIN: "admin-user",
  POS: "point-of-sale-user",
};

export const userTypeOf = (user) =>
  [USER_TYPES.ADMIN, USER_TYPES.POS].includes(user?.userType) ? user.userType : "unclassified";

export const hasUserPermission = (user, permission) => {
  const values = user?.role?.permissions ?? user?.permissions ?? [];
  return Array.isArray(values) ? (values.includes("*") || values.includes(permission)) : (values?.["*"] === true || values?.[permission] === true);
};

export const hasAnyUserPermission = (user, permissions = []) =>
  permissions.some((permission) => hasUserPermission(user, permission));

export const COMPANY_PERMISSIONS = [
  "reports.dashboard", "inventory.view", "inventory.create", "inventory.edit", "inventory.import",
  "inventory.adjust", "inventory.transfer", "inventory.stock_in", "inventory.stock_out",
  "sales.view", "sales.refund", "purchase.view", "purchase.create", "purchase.receive",
  "finance.view", "finance.expense", "finance.invoice", "customer.view", "supplier.view",
  "staff.view", "staff.create", "settings.tax", "reports.view", "branch.view",
];

export const canOpenCompanyAccount = (accessType, user) =>
  accessType === "CLIENT_ACCESS" ||
  (accessType === "USER_ACCESS" && userTypeOf(user) === USER_TYPES.ADMIN &&
    hasAnyUserPermission(user, COMPANY_PERMISSIONS));

export const canOpenClientProfile = (accessType, user) =>
  accessType === "CLIENT_ACCESS" ||
  (accessType === "USER_ACCESS" && userTypeOf(user) === USER_TYPES.ADMIN &&
    hasUserPermission(user, "company.profile.view"));

export const canOpenPOS = (accessType, user) =>
  accessType === "CLIENT_ACCESS" ||
  (accessType === "USER_ACCESS" && userTypeOf(user) !== "unclassified" &&
    ["sales.open_register", "sales.create", "inventory.view"].every(p => hasUserPermission(user, p)));

export const userHomePath = (accessType, user, client) =>
  accessType === "USER_ACCESS" && userTypeOf(user) === USER_TYPES.POS
    ? "/clients/workspace/terminal-login"
    : canOpenCompanyAccount(accessType, user)
    ? `/advance-companies-management-system/company-account?notation=${encodeURIComponent(client?._id || "")}`
    : "/clients/users/workspace";

// Match the existing CompanyAccount menu hooks and backend permission vocabulary.
export const COMPANY_SECTION_PERMISSIONS = {
  dashboard: ["reports.dashboard", "inventory.view", "sales.view", "purchase.view"],
  sales: ["sales.view"], quatations: ["sales.view"], discount: ["sales.view"],
  subscriptions: ["sales.view"], draft: ["sales.view"], sell_return: ["sales.refund"],
  items: ["inventory.view"], "item-explore-display": ["inventory.view"],
  "add-new-item": ["inventory.create"], "edit-item-display": ["inventory.edit"],
  imports: ["inventory.import"], price_groups: ["inventory.price.view"],
  units: ["inventory.view"], brands: ["inventory.view"], categories: ["inventory.view"],
  taxrate: ["settings.tax"], variations: ["inventory.view"], services: ["inventory.view"],
  orders: ["purchase.view"], recieves: ["purchase.receive", "purchase.view"],
  returns: ["purchase.return", "purchase.view"], deliveries: ["inventory.stock_out"],
  transfers: ["inventory.transfer"], openingstock: ["inventory.stock_in"],
  reconciliation: ["inventory.adjust"], adjusment: ["inventory.adjust"],
  product_services: ["inventory.stock_out"], expenses: ["finance.view", "finance.expense"],
  invoicing: ["finance.view", "finance.invoice"], production: ["inventory.create"],
  reports_main: ["reports.dashboard", "reports.view"],
  profile: ["company.profile.view"], users: ["staff.view"], Staff: ["staff.view"],
  setting: ["company.profile.view"], language: ["company.profile.view"],
  "place-new-order": ["purchase.create"], "purchase-order-preview": ["purchase.view"],
};
export const isCompanySectionAllowed = (accessType, user, section) => {
  if (accessType === "CLIENT_ACCESS") return true;
  if (userTypeOf(user) !== USER_TYPES.ADMIN) return false;
  const needed = COMPANY_SECTION_PERMISSIONS[section];
  // Internal help pages do not grant access to business records.
  if (!needed) return ["faqs", "contact-support", "system-guide"].includes(section);
  return hasAnyUserPermission(user, needed);
};
